import { FUEL_LEVELS, GENERAL_STATES, LIMITS, DEFAULT_SETTINGS } from './inspection/constants';

// Pure helpers of the check-in / check-out forms (no React, no network). The server stays the only authority: these rules
// give early feedback and shape the input sent to saveInspectionDraft / submitCheckout. Message keys are keys of
// mobile_languages/web/<lang>.json, resolved by the components with locale/jsonMessages.

export const FUEL_OPTIONS = Object.freeze(FUEL_LEVELS.map((value, index) => ({
  value,
  index,
  labelKey: {
    reserve: 'inspFuelReserve',
    quarter: 'inspFuelQuarter',
    half: 'inspFuelHalf',
    three_quarters: 'inspFuelThreeQuarters',
    full: 'inspFuelFull'
  }[value]
})));

// The state is shown with an icon AND a text (never by colour alone).
export const GENERAL_STATE_OPTIONS = Object.freeze(GENERAL_STATES.map((value) => ({
  value,
  labelKey: { clean: 'inspStateClean', average: 'inspStateAverage', dirty: 'inspStateDirty' }[value],
  hintKey: { clean: 'inspStateCleanHint', average: 'inspStateAverageHint', dirty: 'inspStateDirtyHint' }[value],
  icon: { clean: 'ok', average: 'minus', dirty: 'alert' }[value]
})));

export const DISPUTE_REASONS = Object.freeze([
  { value: 'damage', labelKey: 'inspReasonDamage' },
  { value: 'responsibility', labelKey: 'inspReasonResponsibility' },
  { value: 'settlement', labelKey: 'inspReasonSettlement' },
  { value: 'other', labelKey: 'inspReasonOther' }
]);

export const FORM_LIMITS = Object.freeze({
  comment: LIMITS.commentMax,
  description: LIMITS.descriptionMax,
  motive: LIMITS.motiveMax,
  statement: LIMITS.statementMax,
  mileage: LIMITS.mileageMax,
  photoMax: LIMITS.photoMax
});

// "45 230", "45,230" or "45.230" -> 45230. Anything else (empty, decimals, letters, negative) -> null.
export function parseMileage(text) {
  if (typeof text === 'number') return Number.isInteger(text) && text >= 0 && text <= LIMITS.mileageMax ? text : null;
  if (typeof text !== 'string') return null;
  const compact = text.replace(/[\s  ]/g, '');
  if (!compact) return null;
  const digits = /^\d{1,3}([.,]\d{3})+$/.test(compact) ? compact.replace(/[.,]/g, '') : compact;
  if (!/^\d+$/.test(digits)) return null;
  const value = Number(digits);
  return value <= LIMITS.mileageMax ? value : null;
}

export const fuelIndex = (value) => FUEL_LEVELS.indexOf(value);

export function emptyForm(type) {
  return type === 'checkin'
    ? { generalState: '', mileage: '', fuelLevel: '', keysConfirmed: false, comment: '', checklist: [] }
    : { hasDamage: null, damageDescription: '', mileage: '', fuelLevel: '', keysConfirmed: false, comment: '' };
}

// Server inspection -> form values (used to prefill a draft, a modification and the owner damage declaration).
export function formFromInspection(inspection, type, checklistItems) {
  const base = emptyForm(type);
  if (!inspection) {
    if (type === 'checkin') base.checklist = (checklistItems || []).map((item) => ({ key: item.key, present: false }));
    return base;
  }
  const form = {
    ...base,
    mileage: inspection.mileage === null || inspection.mileage === undefined ? '' : String(inspection.mileage),
    fuelLevel: inspection.fuelLevel || '',
    keysConfirmed: Boolean(inspection.keysConfirmed),
    comment: inspection.comment || ''
  };
  if (type === 'checkin') {
    form.generalState = inspection.generalState || '';
    const answers = {};
    (inspection.checklist || []).forEach((row) => { answers[row.key] = Boolean(row.present); });
    form.checklist = (checklistItems || []).map((item) => ({ key: item.key, present: Boolean(answers[item.key]) }));
  } else {
    form.hasDamage = inspection.hasDamage === undefined ? null : inspection.hasDamage;
    form.damageDescription = inspection.damageDescription || '';
  }
  return form;
}

const trimmed = (text) => (typeof text === 'string' ? text.trim() : '');

// Early validation. Returns [{ field, messageKey }] (empty = valid).
// options: { type, photoCount, settings: { photoMin, photoMax }, checkinMileage }
export function validateForm(form, options) {
  const { type } = options;
  const settings = { ...DEFAULT_SETTINGS, ...(options.settings || {}) };
  const errors = [];
  const add = (field, messageKey) => errors.push({ field, messageKey });
  const source = form || {};

  const mileage = parseMileage(source.mileage);
  if (mileage === null) add('mileage', 'inspErrMileage');
  else if (type === 'checkout' && Number.isFinite(options.checkinMileage) && mileage < options.checkinMileage) add('mileage', 'inspErrMileageDecreasing');

  if (!FUEL_LEVELS.includes(source.fuelLevel)) add('fuelLevel', 'inspErrFuel');
  if (trimmed(source.comment).length > LIMITS.commentMax) add('comment', 'inspErrCommentTooLong');

  const photoCount = Number(options.photoCount) || 0;
  if (type === 'checkin') {
    if (!GENERAL_STATES.includes(source.generalState)) add('generalState', 'inspErrGeneralState');
    if (photoCount < settings.photoMin) add('photos', 'inspErrPhotoMin');
  } else {
    if (typeof source.hasDamage !== 'boolean') add('hasDamage', 'inspErrDamageAnswer');
    if (source.hasDamage === true) {
      if (!trimmed(source.damageDescription)) add('damageDescription', 'inspErrDamageDescription');
      else if (trimmed(source.damageDescription).length > LIMITS.descriptionMax) add('damageDescription', 'inspErrCommentTooLong');
      if (photoCount < settings.photoMin) add('photos', 'inspErrPhotoMin');
    }
  }
  if (photoCount > Math.min(settings.photoMax, LIMITS.photoMax)) add('photos', 'inspErrPhotoMax');
  return errors;
}

// Form -> input of saveInspectionDraft / submitCheckout. Partial values are allowed for drafts.
export function buildInspectionInput(form, type, { draft = false } = {}) {
  const source = form || {};
  const input = {};
  const mileage = parseMileage(source.mileage);
  if (mileage !== null) input.mileage = mileage;
  if (FUEL_LEVELS.includes(source.fuelLevel)) input.fuelLevel = source.fuelLevel;
  input.keysConfirmed = Boolean(source.keysConfirmed);
  input.comment = trimmed(source.comment);
  if (type === 'checkin') {
    if (GENERAL_STATES.includes(source.generalState)) input.generalState = source.generalState;
    if (Array.isArray(source.checklist) && (!draft || source.checklist.length)) {
      input.checklist = source.checklist.map((row) => ({ key: row.key, present: Boolean(row.present) }));
    }
  } else {
    if (typeof source.hasDamage === 'boolean') input.hasDamage = source.hasDamage;
    input.damageDescription = source.hasDamage === false ? '' : trimmed(source.damageDescription);
  }
  return input;
}

// Sorted ids of the photos currently displayed (the server keeps the order given here).
export const photoIdsOf = (photos) => (photos || []).map((photo) => photo.id).filter((id) => Number.isInteger(id));

// Remaining photo slots (never negative).
export const remainingSlots = (count, settings) => Math.max(0, Math.min((settings && settings.photoMax) || LIMITS.photoMax, LIMITS.photoMax) - (Number(count) || 0));

// Fuel gauge change in steps between the check-in and the check-out (display only, the server computes the comparison).
export const fuelDelta = (from, to) => (fuelIndex(from) < 0 || fuelIndex(to) < 0 ? null : fuelIndex(to) - fuelIndex(from));

// A failed validation / save may be a version conflict: the caller reloads and compares the version it displayed.
export const isVersionConflict = (displayedVersion, reloaded) => Boolean(reloaded && Number(reloaded.version) !== Number(displayedVersion));

// Review (R11): 1 to 5 stars and a text. Returns an error key or null.
export function validateReview({ rating, content }) {
  if (!Number.isFinite(rating) || rating < 1 || rating > 5) return 'inspErrReviewRating';
  if (!trimmed(content)) return 'inspErrReviewText';
  if (trimmed(content).length > LIMITS.commentMax) return 'inspErrCommentTooLong';
  return null;
}

// Dispute opening / statement text.
export function validateStatement(text, max = LIMITS.statementMax) {
  const value = trimmed(text);
  if (!value) return 'inspErrStatementRequired';
  if (value.length > max) return 'inspErrCommentTooLong';
  return null;
}

// Admin decision (R19, R20). `hasDeposit` comes from the dispute (server value). Amounts are only intentions: the server recomputes.
export function validateDecision({ type, motive, retainedAmount, requestedAmount, deposit }) {
  const hasDeposit = Number(deposit) > 0;
  const allowed = hasDeposit ? ['release_all', 'retain_part', 'retain_all'] : ['no_action', 'request_indemnity'];
  if (!allowed.includes(type)) return 'inspErrDecisionType';
  if (!trimmed(motive)) return 'inspErrDecisionMotive';
  if (trimmed(motive).length > LIMITS.motiveMax) return 'inspErrCommentTooLong';
  if (type === 'retain_part') {
    const amount = Number(retainedAmount);
    if (!Number.isFinite(amount) || amount <= 0 || amount >= Number(deposit)) return 'inspErrDecisionAmount';
  }
  if (type === 'request_indemnity') {
    const amount = Number(requestedAmount);
    if (!Number.isFinite(amount) || amount <= 0) return 'inspErrDecisionAmount';
  }
  return null;
}

// Arguments of decideDispute: only the intentions the type needs (the server recomputes every amount).
export function buildDecisionArgs({ disputeId, type, motive, retainedAmount, requestedAmount, attachments }) {
  const args = { disputeId: Number(disputeId), type, motive: trimmed(motive), attachments: (attachments || []).filter((id) => Number.isInteger(id)) };
  if (type === 'retain_part') args.retainedAmount = Number(retainedAmount);
  if (type === 'request_indemnity') args.requestedAmount = Number(requestedAmount);
  return args;
}

// Preview only (R18): the balance displayed while typing. The figures of the decision come from the server response.
export function previewBalance(deposit, retained) {
  const total = Number(deposit);
  const part = Number(retained);
  if (!Number.isFinite(total) || !Number.isFinite(part) || part <= 0 || part >= total) return null;
  return Math.round((total - part) * 100) / 100;
}
